import {
    Button,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
    Label,
} from "@elit-hair/elit-ui";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { IconSelector } from "@/components/selectors/icon-selector";
import { PRESET_ICONS } from "@/components/selectors/icons";
import type { CustomLink } from "@/providers/general/settings-provider";

const DEFAULT_ICON_NAME = PRESET_ICONS[0].name;

export type CustomLinkFormData = {
    label: string;
    url: string;
    iconName: string;
};

type CustomLinkDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    editingLink: CustomLink | null;
    onSave: (data: CustomLinkFormData) => void;
};

export function CustomLinkDialog({ open, onOpenChange, editingLink, onSave }: CustomLinkDialogProps) {
    const { t } = useTranslation();
    const [label, setLabel] = useState("");
    const [url, setUrl] = useState("");
    const [iconName, setIconName] = useState(DEFAULT_ICON_NAME);

    const isEdit = editingLink !== null;

    const applyFormValues = useCallback((values: CustomLinkFormData) => {
        setLabel(values.label);
        setUrl(values.url);
        setIconName(values.iconName || DEFAULT_ICON_NAME);
    }, []);

    useEffect(() => {
        if (open) {
            const values: CustomLinkFormData = editingLink
                ? {
                      label: editingLink.label,
                      url: editingLink.url,
                      iconName: editingLink.iconName || DEFAULT_ICON_NAME,
                  }
                : { label: "", url: "", iconName: DEFAULT_ICON_NAME };
            applyFormValues(values);
        }
    }, [open, editingLink, applyFormValues]);

    const handleSave = useCallback(() => {
        const data: CustomLinkFormData = {
            label: label.trim(),
            url: url.trim(),
            iconName,
        };
        if (!data.label || !data.url) return;

        onSave(data);
        onOpenChange(false);
    }, [label, url, iconName, onSave, onOpenChange]);

    const isValid = label.trim().length > 0 && url.trim().length > 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{isEdit ? t("custom_link_edit") : t("custom_link_add")}</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                        <Label htmlFor="custom-link-label">{t("custom_link_label")}</Label>
                        <Input
                            id="custom-link-label"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder={t("custom_link_label_placeholder")}
                        />
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="custom-link-url">{t("custom_link_url")}</Label>
                        <Input
                            id="custom-link-url"
                            type="url"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://"
                        />
                    </div>
                    <div className="grid gap-2">
                        <Label>{t("custom_link_icon")}</Label>
                        <IconSelector selectedIcon={iconName} onSelect={setIconName} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t("cancel")}
                    </Button>
                    <Button onClick={handleSave} disabled={!isValid}>
                        {isEdit ? t("save") : t("custom_link_add")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
